/**
 * The LAMA respondent route, declared ONCE for both sides that need it.
 *
 * The shell router and the notification producer each used to spell this path
 * out: `LamaRouter.tsx` declared `path="assess"` and
 * `reminder-evaluation.ts` concatenated `"/app/lama/assess?waveId=" + waveId`
 * in the backend. Two independent declarations of one route means renaming it
 * in the router silently leaves every newly sent reminder pointing at a path
 * that no longer resolves — and a notification link is exactly the thing
 * nobody re-tests after a route rename.
 *
 * Lives in `@playbookai/types` because it is the only package both a product
 * frontend and a backend module already depend on. It carries no behaviour,
 * just the shape of the URL, so neither side gains a dependency on the other.
 */
/** The respondent route's segment, relative to the LAMA module base. */
export const LAMA_ASSESS_SEGMENT = "assess";
/** The LAMA module's shell base path. NOT the tab-scoped
 *  `/app/lama/:instanceId` — the assess route is a deliberate literal sibling
 *  of `:instanceId`, because a respondent reaches it without a workspace tab. */
export const LAMA_MODULE_BASE = "/app/lama";
/** The query parameter the assess route resolves a wave from. */
export const LAMA_ASSESS_WAVE_PARAM = "waveId";
/**
 * The My Responses segment, relative to the tab-scoped
 * `/app/lama/:instanceId`. Declared beside the assess segment for the same
 * reason: the module layout's navigation, the role-aware index redirect and
 * the router each name it, and one spelling keeps them from drifting.
 */
export const LAMA_RESPONSES_SEGMENT = "responses";
/**
 * Where the assess route's Close goes back to, when it was opened from inside
 * the module.
 *
 * It carries the shell's opaque TAB INSTANCE id and nothing else — never a
 * path, never a URL. The assess route is a literal sibling of `:instanceId`, so
 * it has no instance of its own to return to, and a notification link has no
 * origin at all; without this the control could only guess. A caller-supplied
 * PATH would be an open redirect, and a caller-supplied resource id would be a
 * second way to address a resource: one opaque token, interpolated into one
 * fixed route shape by `lamaPath`, is neither.
 */
export const LAMA_ASSESS_RETURN_PARAM = "from";
/**
 * The assess route's href for one wave, keyed on the wave's real UUID and never
 * its display `code` — the assess route resolves `?waveId=` against the id.
 *
 * `returnInstanceId` is the shell tab the respondent came from, so Close lands
 * back on THAT My Responses rather than opening a second copy of it. Omitted
 * for a link that has no origin — a reminder, a shared URL — where Close falls
 * back to opening My Responses through the shell.
 */
export function lamaAssessmentHref(waveId, returnInstanceId, assignmentId) {
    const base = `${LAMA_MODULE_BASE}/${LAMA_ASSESS_SEGMENT}?${LAMA_ASSESS_WAVE_PARAM}=${encodeURIComponent(waveId)}`;
    const href = returnInstanceId
        ? `${base}&${LAMA_ASSESS_RETURN_PARAM}=${encodeURIComponent(returnInstanceId)}`
        : base;
    return assignmentId
        ? `${href}&assignmentId=${encodeURIComponent(assignmentId)}`
        : href;
}
/**
 * Whether a `?from=` value may be used to rebuild a route.
 *
 * A shell tab instance id is an opaque token the shell mints; anything with a
 * slash, a scheme, a dot segment or whitespace is not one, and would be an
 * attempt to steer navigation through a query parameter. Rejecting rather than
 * sanitising keeps the failure visible: an unusable value falls back to the
 * ordinary "open My Responses" path instead of half-working.
 */
export function isLamaReturnInstanceId(value) {
    return (value !== null &&
        value !== LAMA_ASSESS_SEGMENT &&
        /^[A-Za-z0-9_-]{1,64}$/.test(value));
}
//# sourceMappingURL=lama-routes.js.map